import { zLogRocket } from '@zoll/logrocket-react-lib'

interface LoggingDetail {
  hasCallerInfo: boolean
  timeStamp: string
  logLevel: string
  functionName?: string
  filename?: string
  line?: string
  column?: string
  message: string
}

export default class LoggerService {
  public static readonly LINE = '--------------------------------------'

  private static readonly DO_NOT_LOG_TO_LOG_ROCKET = false

  private static _INTERNAL_LOG_LEVEL_TO_PRINTED_LOG_LEVEL_MAP: Map<string, string> = new Map()

  // Log levels
  // These must match the function names (case-insensitively)
  private static readonly SENSITIVE = 'SENSITIVE'
  private static readonly APICALL = 'APICALL'
  private static readonly LOG = 'LOG'
  private static readonly INFO = 'INFO'
  private static readonly WARN = 'WARN'
  private static readonly DEBUG = 'DEBUG'
  private static readonly ERROR = 'ERROR'

  // prettier-ignore
  static initialize(): void {
    LoggerService._INTERNAL_LOG_LEVEL_TO_PRINTED_LOG_LEVEL_MAP.set(LoggerService.SENSITIVE, 'SENSITIVE')
    LoggerService._INTERNAL_LOG_LEVEL_TO_PRINTED_LOG_LEVEL_MAP.set(LoggerService.APICALL,   'APICALL  ')
    LoggerService._INTERNAL_LOG_LEVEL_TO_PRINTED_LOG_LEVEL_MAP.set(LoggerService.LOG,       'LOG      ')
    LoggerService._INTERNAL_LOG_LEVEL_TO_PRINTED_LOG_LEVEL_MAP.set(LoggerService.INFO,      'INFO     ')
    LoggerService._INTERNAL_LOG_LEVEL_TO_PRINTED_LOG_LEVEL_MAP.set(LoggerService.WARN,      'WARN     ')
    LoggerService._INTERNAL_LOG_LEVEL_TO_PRINTED_LOG_LEVEL_MAP.set(LoggerService.DEBUG,     'DEBUG    ')
    LoggerService._INTERNAL_LOG_LEVEL_TO_PRINTED_LOG_LEVEL_MAP.set(LoggerService.ERROR,     'ERROR    ')
  }

  // ----------------------------------------------------
  // Note: Do NOT change the name of the 'sensitiveData' parameter.
  // If it needs to change, then the same string must be added into
  // sanitizers.anywhere.sanitizeFuzzyMatch array within src\features\zLogRocket\customizeSanitizers.ts
  //
  // The purpose of the optional 'context' parameter is so for those viewing sessions in Log Rocket
  // at least have an idea of what the intended log was for, while hiding the sensitiveData.
  // ----------------------------------------------------
  static sensitive(sensitiveData: any, context?: string) {
    LoggerService._sensitive(sensitiveData, LoggerService.SENSITIVE, context)
  }

  static apiCall(type: string, url: string) {
    const apiMessage = `Making a ${type} call to ${url}`
    LoggerService._apiCall(apiMessage, LoggerService.APICALL)
  }

  static log(message: string) {
    LoggerService._log(message, LoggerService.LOG)
  }

  static info(message: string) {
    LoggerService._info(message, LoggerService.INFO)
  }

  static warn(message: string) {
    LoggerService._warn(message, LoggerService.WARN)
  }

  static debug(message: string) {
    LoggerService._debug(message, LoggerService.DEBUG)
  }

  static error(message: string) {
    LoggerService._error(message, LoggerService.ERROR)
  }

  static errorObj(message: string, error: any) {
    let errMessage = JSON.stringify(error)
    if (error instanceof Error) {
      errMessage = error.message
    }

    const fullMessage = `${message} - Error: ${errMessage}`

    LoggerService._error(fullMessage, LoggerService.ERROR)
  }

  private static _sensitive(sensitiveData: any, callerName: string, context?: string) {
    if (context) {
      LoggerService._log(context, callerName)
    }

    let message = sensitiveData
    if (typeof message !== 'string') {
      message = JSON.stringify(sensitiveData, null, 2)
    }

    LoggerService._log(message, callerName, LoggerService.DO_NOT_LOG_TO_LOG_ROCKET)
  }

  private static _apiCall(message: string, callerName: string) {
    LoggerService._info(message, callerName)
  }

  private static _log(message: string, callerName: string, logToLogRocket = true) {
    const logLine = LoggerService.getLogLine(message, callerName)
    console.log(logLine)

    if (logToLogRocket) {
      zLogRocket.log(logLine)
    }
  }

  private static _info(message: string, callerName: string, logToLogRocket = true) {
    const logLine = LoggerService.getLogLine(message, callerName)
    console.info(logLine)

    if (logToLogRocket) {
      zLogRocket.info(logLine)
    }
  }

  private static _warn(message: string, callerName: string, logToLogRocket = true) {
    const logLine = LoggerService.getLogLine(message, callerName)
    console.warn(logLine)

    if (logToLogRocket) {
      zLogRocket.warn(logLine)
    }
  }

  private static _debug(message: string, callerName: string, logToLogRocket = true) {
    const logLine = LoggerService.getLogLine(message, callerName)
    console.debug(logLine)

    if (logToLogRocket) {
      zLogRocket.debug(logLine)
    }
  }

  private static _error(message: string, callerName: string, logToLogRocket = true) {
    const logLine = LoggerService.getLogLine(message, callerName)
    console.error(logLine)

    if (logToLogRocket) {
      zLogRocket.error(logLine)
    }
  }

  private static getLogLine(message: string, logCallerName: string) {
    return message
    const loggingDetail = LoggerService.getLoggingDetail(message, logCallerName)
    return LoggerService.getLogLineFromLoggingDetail(loggingDetail)
  }

  private static getLoggingDetail(message: string, logCallerName: string): LoggingDetail {
    const date = new Date()
    const timeStamp = date
      .toLocaleString(undefined, {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short',
        hour12: true,
      })
      .replace(',', '')

    return {
      hasCallerInfo: false,
      timeStamp,
      logLevel: logCallerName,
      message,
    }
  }

  private static getLogLineFromLoggingDetail(loggingDetail: LoggingDetail) {
    const logLevel = loggingDetail.logLevel
    const mappedLogLevel = LoggerService._INTERNAL_LOG_LEVEL_TO_PRINTED_LOG_LEVEL_MAP.get(logLevel)
    const printedLogLevel = mappedLogLevel ? mappedLogLevel : logLevel

    if (!loggingDetail.hasCallerInfo) {
      return `${loggingDetail.timeStamp} [${printedLogLevel}] - ${loggingDetail.message}`
    }

    const callerInfo = `${loggingDetail.functionName}(${loggingDetail.filename}:${loggingDetail.line}:${loggingDetail.column})`
    return `${loggingDetail.timeStamp} [${printedLogLevel}] ${callerInfo} - ${loggingDetail.message}`
  }
}
